import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  switchMap,
  withLatestFrom
} from 'rxjs';
import { select, Store } from '@ngrx/store';
import { box } from 'ngrx-forms';
import flow from 'lodash-es/flow';
import { TranslocoService } from '@jsverse/transloco';
import { VshcDialogWrapperComponent } from '@vshosting/components';
import {
  selectClientServiceBaseDataAllowedLocations,
  DomainEntity,
  selectCountries,
  selectRemoteLogTypes
} from '@vshosting/cdn/core';
import { FEATURE_NAME } from './edit-domain-dialog.constant';
import { EditDomainDialogForm } from './edit-domain-dialog.form';
import {
  cleanupCacheData,
  cleanupGeoIpData,
  cleanupLocations,
  cleanupRemoteLogData
} from './edit-domain-dialog.utils';
import { EditDomainFormComponent } from './components/edit-domain-form';
import { orderBy } from 'lodash-es';
import {
  selectVshcDialogState,
  vshcDialogClose,
  formValueOnValid,
  ZefReactiveComponentBase,
  ProgressErrorContainer
} from '@vshosting/core';
import { CdnCountry } from '@vshosting/models';

@Component({
    selector: 'vshcdn-edit-domain-dialog',
    templateUrl: './edit-domain-dialog.feature.html',
    styleUrls: ['./edit-domain-dialog.feature.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatIconModule,
        MatButtonModule,
        ProgressErrorContainer,
        EditDomainFormComponent,
        VshcDialogWrapperComponent
    ]
})
export class EditDomainDialogFeature extends ZefReactiveComponentBase {

  // # Deps
  #store = inject(Store);
  #domainEntity = inject(DomainEntity);
  #translocoService = inject(TranslocoService);

  // # Forms
  editDomainForm = inject(EditDomainDialogForm);

  // # Event Streams
  onUpdate$ = new Subject<void>();
  onClose$ = new Subject<void>();
  onRemoveCountryCode$ = new Subject<string>();

  // # Data
  // -- sync
  updateActionKey = this.#domainEntity.updateOne.type;

  // -- async
  dialog$ = this.#store.pipe(select(selectVshcDialogState(FEATURE_NAME)));
  open$ = this.dialog$.pipe(map((d) => d?.state));
  activeLang$ = this.#translocoService.langChanges$;
  domainId$ = this.dialog$.pipe(
    map((d) => d?.meta),
    filter((d) => !!d),
    distinctUntilChanged()
  );
  domain$ = this.domainId$.pipe(
    switchMap((id) => this.#domainEntity.entityById$(id)),
    filter((d) => !!d)
  );
  allowedLocations$ = this.#store.pipe(
    select(selectClientServiceBaseDataAllowedLocations)
  );
  remoteLogTypes$ = this.#store.pipe(select(selectRemoteLogTypes));
  countryList$ = this.#store.pipe(select(selectCountries));
  translations$ = this.#translocoService.selectTranslateObject('editDomainDialog');
  generalTranslations$ = this.#translocoService.selectTranslateObject('general');
  sortedCountryList$ = combineLatest([
    this.activeLang$,
    this.countryList$
  ]).pipe(
    map(([ activeLang, countryList ]) => this.#sortCountriesByLanguage(countryList, activeLang))
  )

  // # Resolver
  state = this.$connect({
    editDomainFormState: this.editDomainForm.state$,
    allowedLocations: this.allowedLocations$,
    domain: this.domain$,
    open: this.open$,
    countryList: this.countryList$,
    activeLang: this.activeLang$,
    remoteLogTypes: this.remoteLogTypes$,
    translations: this.translations$,
    generalTranslations: this.generalTranslations$,
    sortedCountryList: this.sortedCountryList$
  });

  // # Action Streams
  #closeAction$ = this.onClose$.pipe(
    map(() => vshcDialogClose({ key: FEATURE_NAME }))
  );
  #updateAction$ = this.onUpdate$.pipe(
    formValueOnValid(this.editDomainForm),
    withLatestFrom(this.domainId$),
    map(([ v, id ]) => this.#domainEntity.updateOne(
      id,
      flow([
        cleanupRemoteLogData,
        cleanupGeoIpData,
        cleanupCacheData,
        cleanupLocations
      ])(v),
      { tag: FEATURE_NAME }
    ))
  );
  #removeCountryCodeAction$ = this.onRemoveCountryCode$.pipe(
    withLatestFrom(this.editDomainForm.value$),
    map(([ code, value ]) => this.editDomainForm.setValue({
      ...value,
      geoIpCountries: box(value.geoIpCountries.value.filter((c) => c !== code))
    }))
  );

  constructor() {
    super();

    this.$dispatchActions([
      this.#closeAction$,
      this.#updateAction$,
      this.#removeCountryCodeAction$
    ]);
  }

  #sortCountriesByLanguage(countries: CdnCountry[], activeLang: string ) {
    return orderBy(countries, country => country.nameJsonObject[activeLang]);
  }
}
